import { useMemo } from 'react';

import type { DrawInstruction } from '@aline/common';
import { Apollo } from 'src/gql/generated';
import { useDeepCompareMemo } from 'use-deep-compare';

export type AssetMap = ReturnType<typeof useAssets>;

/**
 * Load assets used by canvas image draw instructions into an asset map.
 */
export function useAssets(drawInstructions: DrawInstruction[]) {
  const fileIds = useMemo(
    () => Array.from(new Set(drawInstructions.flatMap(getDrawInstructionImageIds))),
    [drawInstructions]
  );

  /**
   * Fetch the asset metadata for these files. If the set of file IDs changes, use the previous
   * result while the next result is being fetched so that already loaded files remain visible.
   */
  const { previousData, data = previousData } = Apollo.useGetFilesQuery({
    skip: fileIds.length === 0,
    variables: { fileIds },
  });

  const assetMap = useDeepCompareMemo(() => {
    if (fileIds.length === 0) return new Map<string, Apollo.GetFilesQuery['files'][number]>();

    return new Map<string, Apollo.GetFilesQuery['files'][number]>(
      data?.files.map(file => [file.id, file]) ?? []
    );
  }, [data?.files, fileIds]);

  return assetMap;
}

function getDrawInstructionImageIds(drawInstruction: DrawInstruction) {
  if (drawInstruction.type !== 'image') return [];
  return [drawInstruction.imageId];
}
